import { Link } from 'react-router';
import type { AuditViaDTO } from '@contracts/common';
import { MarkTip } from '@/components/MarkTip';
import { actorLabel, type AssistedSegment } from '@/lib/actor';
import { useAssistantOptional } from './AssistantProvider';
import { missionHref, orderHref } from './missionLinks';

/** R13: always underlined, like HistoryPage.tsx:316's inline links — the colour alone would not say "link". */
const LINK = 'rounded-sm text-[hsl(var(--h-primary))] underline decoration-[hsl(var(--h-primary)/0.35)] underline-offset-[3px] hover:decoration-current';

/** S8 §6.2 (spec words): what ✦ says beside another person's name. */
export const ASSISTED_MARK = 'Thực hiện qua trợ lý';

/**
 * ✦ beside the name of someone who acted through their assistant (S8 §6.2, mockup Màn 5 `.star`): the reader is told
 * it was assisted, never by which persona. The words are in the text for a screen reader; the tooltip repeats them on
 * hover, keyboard focus and a tap (D42).
 */
export function AssistedMark() {
  return (
    <MarkTip tip={ASSISTED_MARK} className="font-extrabold text-[hsl(var(--h-icon-violet))]" data-assisted-mark="">
      <span aria-hidden="true">✦</span>
      <span className="sr-only">thực hiện qua trợ lý</span>
    </MarkTip>
  );
}

/**
 * A line that names assistants (S8 §6.2, D16, D41): "✦ Mây đang tạo 4 thẻ · An ✦ đang tạo 1 thẻ". Drawn from its
 * segments, never by splitting text on "✦" — a persona or a person's name may hold one. Another person's assistant is
 * "{name} ✦", the ✦ an `AssistedMark` read as "thực hiện qua trợ lý"; the viewer's own opens with a ✦ that only
 * decorates the persona name after it, so it is drawn and not read.
 */
export function AssistedLine({ segments }: { segments: readonly AssistedSegment[] }) {
  return (
    <>
      {segments.map((s, i) => (
        // The segments of one line, never reordered: the index is a stable key.
        <span key={i}>
          {i > 0 && ' · '}
          {s.own ? <><span aria-hidden="true">✦</span>{` ${s.name}`}</> : <>{`${s.name} `}<AssistedMark /></>}
          {s.text}
        </span>
      ))}
    </>
  );
}

/**
 * Who acted (spec §5.5; plan P3 D169). On the viewer's own assistant rows (`via.own`, P2 D94) the persona is a text
 * button that opens the panel on the conversation the write came from; every other row, and every row while Hana is
 * not usable here, reads "Bình (qua Hana)" as plain text when the server sent the persona (the Audit page), and
 * "Bình ✦" when it did not (S8 §6.2: anyone else's row anywhere else). A background run's conversation never opens in the panel
 * (S7 §1.2 item 21, P24): its row links to the mission, or to its standing order on the orders tab (whole-S7 C4), on
 * the Nhiệm vụ page, and is plain text when the run is gone.
 */
export function ActorVia({ name, via }: { name: string; via: AuditViaDTO | null }) {
  const ui = useAssistantOptional();
  // S8 §6.2: no persona sent means another person's assisted write: "Bình ✦", never a link.
  if (via !== null && via.persona === undefined && via.own === null) return <>{`${name} `}<AssistedMark /></>;
  if (via === null || via.own === null || ui === null) return <>{actorLabel(name, via)}</>;
  const { own } = via;
  // A D44 row stored without a persona: the actor's own link still opens, worded without a name.
  const said = via.persona === undefined ? '(qua trợ lý)' : `(qua ${via.persona})`;
  // The name is one text node: a block-level sr-only child would put a space before its comma.
  const label = (goes: string) => (
    <>
      <span aria-hidden="true">{said}</span>
      <span className="sr-only">{`${said}, ${goes}`}</span>
    </>
  );
  if (own.run === true) {
    const to = own.missionId !== undefined ? missionHref(own.missionId)
      : own.orderId !== undefined ? orderHref(own.orderId) : null;
    if (to === null) return <>{actorLabel(name, via)}</>;
    return (
      <span>
        {`${name} `}
        <Link to={to} className={LINK} title={own.missionId !== undefined ? 'Mở nhiệm vụ này' : 'Mở lệnh thường trực'}>
          {label(own.missionId !== undefined ? 'mở nhiệm vụ' : 'mở lệnh thường trực')}
        </Link>
      </span>
    );
  }
  const { conversationId } = own;
  return (
    <span>
      {`${name} `}
      <button type="button" title="Mở hội thoại này"
        onClick={(e) => ui.openConversation(conversationId, e.currentTarget)} className={LINK}>
        {label('mở hội thoại')}
      </button>
    </span>
  );
}
